import { useState, useEffect, useMemo } from "react";

// SVG Leaf shapes - natural botanical leaves only
const createLeafSVG = (color, opacity, type) => {
  switch(type) {
    case 0: // Simple oval leaf
      return (
        <svg viewBox="0 0 20 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M10 0C4 6 1 14 1 22C1 28 4 32 10 32C16 32 19 28 19 22C19 14 16 6 10 0Z" 
            fill={color} 
            fillOpacity={opacity}
          />
          <path 
            d="M10 4V28" 
            stroke={color} 
            strokeOpacity={opacity * 1.5}
            strokeWidth="0.4"
          />
        </svg>
      );
    case 1: // Pointed leaf
      return (
        <svg viewBox="0 0 18 30" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M9 0C3 8 0 16 2 24C4 28 9 30 9 30C9 30 14 28 16 24C18 16 15 8 9 0Z" 
            fill={color} 
            fillOpacity={opacity}
          />
          <path 
            d="M9 4V26" 
            stroke={color} 
            strokeOpacity={opacity * 1.4}
            strokeWidth="0.3"
          />
        </svg>
      );
    case 2: // Round leaf with stem
      return (
        <svg viewBox="0 0 22 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <ellipse 
            cx="11" 
            cy="11" 
            rx="10" 
            ry="10" 
            fill={color} 
            fillOpacity={opacity}
          />
          <path 
            d="M11 2V22" 
            stroke={color} 
            strokeOpacity={opacity * 1.3}
            strokeWidth="0.4"
          />
          <path 
            d="M11 22V28" 
            stroke={color} 
            strokeOpacity={opacity * 1.6}
            strokeWidth="0.8"
          />
        </svg>
      );
    case 3: // Elongated leaf
      return (
        <svg viewBox="0 0 14 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M7 0C2 10 0 18 1 26C2 32 7 36 7 36C7 36 12 32 13 26C14 18 12 10 7 0Z" 
            fill={color} 
            fillOpacity={opacity}
          />
          <path 
            d="M7 6V30" 
            stroke={color} 
            strokeOpacity={opacity * 1.3}
            strokeWidth="0.35"
          />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 20 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M10 0C4 6 1 14 1 22C1 28 4 32 10 32C16 32 19 28 19 22C19 14 16 6 10 0Z" 
            fill={color} 
            fillOpacity={opacity}
          />
        </svg>
      );
  }
};

// Soft natural colors - muted and elegant
const leafColors = [
  "#4A7C59", "#6B8E6B", "#87A889", "#5B7C8D", 
  "#7BA3B5", "#8B7355", "#A68B5B", "#6B8B73",
];

// Single Leaf Component - starts above viewport and falls elegantly
const Leaf = ({ id }) => {
  const config = useMemo(() => {
    const leafType = Math.floor(Math.random() * 4);
    const colorIndex = Math.floor(Math.random() * leafColors.length);
    const size = 12 + Math.random() * 14; // 12-26px
    const opacity = 0.2 + Math.random() * 0.25; // 0.2-0.45 subtle
    const xPos = 5 + Math.random() * 90;
    const duration = 18 + Math.random() * 12; // 18-30s very slow fall
    const delay = Math.random() * 10; // staggered start up to 10s
    const swayAmount = 20 + Math.random() * 40; // 20-60px gentle sway
    const rotateStart = Math.random() * 30 - 15; // -15 to 15 degrees
    const rotateEnd = Math.random() * 60 - 30; // -30 to 30 degrees
    
    return {
      leafType,
      color: leafColors[colorIndex],
      size,
      opacity,
      xPos,
      duration,
      delay,
      swayAmount,
      rotateStart,
      rotateEnd,
    };
  }, [id]);

  const leafSVG = createLeafSVG(config.color, config.opacity, config.leafType);

  return (
    <div
      className="falling-leaf"
      style={{
        left: `${config.xPos}%`,
        width: `${config.size}px`,
        height: `${config.size * 1.5}px`,
        animationDuration: `${config.duration}s`,
        animationDelay: `${config.delay}s`,
        '--sway': `${config.swayAmount}px`,
        '--rotate-start': `${config.rotateStart}deg`,
        '--rotate-end': `${config.rotateEnd}deg`,
      }}
    >
      {leafSVG}
    </div>
  );
};

// Falling Leaves Container Component
export const FallingLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isReady) return;
    // 10 leaves for elegant, non-cluttered effect
    const initialLeaves = Array.from({ length: 10 }, (_, i) => ({
      id: i,
      key: `leaf-${i}-${Date.now()}`,
    }));
    setLeaves(initialLeaves);
  }, [isReady]);

  if (!isReady) return null;

  return (
    <div className="falling-leaves-container">
      {leaves.map((leaf) => (
        <Leaf key={leaf.key} id={leaf.id} />
      ))}
    </div>
  );
};

export default FallingLeaves;
